import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from "@mui/material";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SarIcon from "../icons/SarIcon";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditDocumentIcon from "@mui/icons-material/EditDocument";
import InvestorLoanApplicationModal from "./InvestorLoanApplicationModal";
import InvestorSignDocumentsModal from "./InvestorSignDocumentsModal";
import { useGetInvestorLoansQuery } from "../../redux/api/authApi";

const PAGE_SIZE = 20;

/**
 * Display config per API `status`. Keys are normalized (upper snake case), so
 * `Pending Review`, `pending-review` and `PENDING_REVIEW` all resolve here.
 * Unknown statuses fall back to a neutral badge with the value humanized.
 */
const STATUS_CONFIG = {
  PENDING_REVIEW: {
    label: "Pending Review",
    badgeKey: "warning",
    action: "review",
    icon: <AccessTimeIcon sx={{ fontSize: 12 }} />,
  },
  SIGNATURE_REQUIRED: {
    label: "Signature Required",
    badgeKey: "info",
    action: "sign",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 12 }} />,
  },
  AWAITING_SIGNATURE: {
    label: "Signature Required",
    badgeKey: "info",
    action: "sign",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 12 }} />,
  },
  FINAL_REVIEW: {
    label: "Final Review",
    badgeKey: "info",
    action: "review",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 12 }} />,
  },
  ACTIVE: {
    label: "Active",
    badgeKey: "success",
    action: "track",
    icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
  },
  REJECTED: {
    label: "Rejected",
    badgeKey: "warning",
    action: null,
    icon: <AccessTimeIcon sx={{ fontSize: 12 }} />,
  },
};

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "sign", label: "Sign" },
  { key: "final", label: "Final" },
  { key: "active", label: "Active" },
];

const TAB_HEADINGS = {
  all: "All Loan Applications",
  pending: "Pending Loan Applications",
  sign: "Awaiting Signature",
  final: "Final Review",
  active: "Active Loans",
};

const COLUMNS = ["Loan ID", "Amount", "Collateral", "Applied", "Status", "Action"];

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

/** `PENDING_REVIEW` → `Pending Review`, for statuses not in STATUS_CONFIG. */
const humanizeStatus = (status) =>
  normalizeStatus(status)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ") || "—";

const statusConfig = (status) => STATUS_CONFIG[normalizeStatus(status)] || null;

const formatAmount = (amount, currency = "SAR") => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  return `${currency} ${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const formatApplied = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCollateral = (loan) => {
  const name = loan.assetName || "—";
  const pct = Number(loan.collateralOwnershipPercent);
  if (!Number.isFinite(pct)) return name;
  return `${name} (${pct.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}% ownership)`;
};

const formatTerm = (months) =>
  Number.isFinite(Number(months)) ? `${months} months` : "—";

/** Label above value, as used inside the signature cards. */
const SignField = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 13, color: "text.muted" }}>{label}</Typography>
    <Typography sx={{ fontSize: 16, color: "text.strong", mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

/** Sign-tab card: loan terms plus the DocuSign call to action. */
const SignatureCard = ({ loan, onSign }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 1.5,
      bgcolor: (theme) => theme.palette.accent.blue.bg,
      border: (theme) => `1px solid ${theme.palette.accent.blue.borderFaint}`,
    }}
  >
    <Typography sx={{ fontSize: 16, color: "text.strong" }}>{loan.loanId}</Typography>

    <Box
      sx={{
        mt: 1.5,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        rowGap: 1.5,
        columnGap: 2,
      }}
    >
      <SignField
        label="Loan Amount"
        value={formatAmount(loan.amount, loan.currency)}
      />
      <SignField
        label="Monthly Payment"
        value={formatAmount(loan.monthlyPayment, loan.currency)}
      />
      <SignField
        label="Approved Date"
        value={formatApplied(loan.approvedAt)}
      />
      <SignField label="Loan Term" value={formatTerm(loan.termMonths)} />
    </Box>

    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: (theme) => `1px solid ${theme.palette.border.light}`,
      }}
    >
      <Typography sx={{ fontSize: 13, color: "text.muted" }}>
        Collateral Asset
      </Typography>
      <Typography sx={{ fontSize: 15, color: "text.strong", mt: 0.25 }}>
        {formatCollateral(loan)}
      </Typography>
    </Box>

    <Button
      fullWidth
      variant="contained"
      onClick={() => onSign?.(loan)}
      startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
      sx={{
        mt: 2,
        height: 44,
        borderRadius: 1,
        fontSize: 15,
        fontWeight: 600,
        textTransform: "none",
        color: "common.white",
        background: (theme) => theme.palette.gradients.blueCyan,
        boxShadow: "none",
        "&:hover": {
          background: (theme) => theme.palette.gradients.blueCyanHover,
          boxShadow: "none",
        },
      }}
    >
      Review &amp; Sign
    </Button>
  </Paper>
);

/** Tinted status badge with a matching 1px border (see `palette.badges`). */
const StatusBadge = ({ status }) => {
  const cfg = statusConfig(status);
  const badgeKey = cfg?.badgeKey || "info";
  return (
    <Chip
      icon={cfg?.icon}
      label={cfg?.label || humanizeStatus(status)}
      size="small"
      sx={{
        height: 22,
        borderRadius: 1.25,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: (theme) => theme.palette.badges[badgeKey].bg,
        color: (theme) => theme.palette.badges[badgeKey].text,
        border: (theme) => `1px solid ${theme.palette.badges[badgeKey].border}`,
        "& .MuiChip-icon": {
          ml: 0.75,
          mr: -0.25,
          color: (theme) => theme.palette.badges[badgeKey].text,
        },
      }}
    />
  );
};

const TabPill = ({ active, label, onClick }) => (
  <Button
    onClick={onClick}
    disableRipple
    sx={{
      flex: 1,
      minWidth: 0,
      height: 29,
      py: 0,
      borderRadius: 1.75,
      fontSize: 14,
      fontWeight: 600,
      textTransform: "none",
      color: "text.strong",
      bgcolor: active ? "background.paper" : "transparent",
      boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
      "&:hover": {
        bgcolor: active ? "background.paper" : "rgba(255,255,255,0.6)",
      },
    }}
  >
    {label}
  </Button>
);

/** Row action: primary for signature-ready loans, outlined/disabled otherwise. */
const RowAction = ({ action }) => {
  const baseSx = {
    height: 32,
    borderRadius: 1.25,
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  if (action === "sign") {
    return (
      <Button
        variant="contained"
        startIcon={<EditDocumentIcon sx={{ fontSize: 16 }} />}
        sx={{
          ...baseSx,
          px: 1.25,
          color: "common.white",
          bgcolor: (theme) => theme.palette.accent.blue.action,
          boxShadow: "none",
          "&:hover": {
            bgcolor: (theme) => theme.palette.accent.blue.actionHover,
            boxShadow: "none",
          },
        }}
      >
        Sign Now
      </Button>
    );
  }

  if (action === "review") {
    return (
      <Button
        variant="outlined"
        disabled
        startIcon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
        sx={{
          ...baseSx,
          px: 1.375,
          "&.Mui-disabled": {
            opacity: 0.5,
            borderColor: (theme) => theme.palette.border.light,
            color: (theme) => theme.palette.text.strong,
          },
        }}
      >
        In Review
      </Button>
    );
  }

  if (action === "track") {
    return (
      <Button
        variant="outlined"
        sx={{
          ...baseSx,
          px: 1.625,
          borderColor: "border.light",
          color: "text.strong",
          bgcolor: "background.paper",
          "&:hover": {
            borderColor: "border.medium",
            bgcolor: "background.subtle",
          },
        }}
      >
        Repay &amp; Track
      </Button>
    );
  }

  return null;
};

const InvestorMortgage = () => {
  const [tab, setTab] = React.useState("all");
  const [applyOpen, setApplyOpen] = React.useState(false);
  const [signLoan, setSignLoan] = React.useState(null);

  const { data, isFetching, isError } = useGetInvestorLoansQuery({
    page: 1,
    limit: PAGE_SIZE,
    tab,
  });

  const payload = data?.data;
  const loans = React.useMemo(
    () => (Array.isArray(payload?.items) ? payload.items : []),
    [payload]
  );
  const counts = payload?.counts || {};
  const signCount = Number(counts.sign) || 0;

  /** Signature-ready loans present in the current tab, for the banner CTAs. */
  const signLoans = loans.filter((loan) => statusConfig(loan.status)?.action === "sign");

  return (
    <Box>
      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 66,
              height: 66,
              flexShrink: 0,
              borderRadius: 2,
              background: (theme) => theme.palette.accent.cyan.tile,
              border: (theme) => `1px solid ${theme.palette.accent.cyan.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountBalanceOutlinedIcon
              sx={{
                fontSize: 32,
                color: (theme) => theme.palette.accent.cyan.deep,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: "text.strong", letterSpacing: -0.5 }}
            >
              My Loans
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Asset-backed loans and applications
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          onClick={() => setApplyOpen(true)}
          startIcon={<SarIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 36,
            px: 1.5,
            borderRadius: 1.25,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            color: "common.white",
            background: (theme) => theme.palette.gradients.cyanBlue,
            boxShadow: "none",
            "&:hover": {
              background: (theme) => theme.palette.gradients.cyanBlueHover,
              boxShadow: "none",
            },
          }}
        >
          Apply for Loan
        </Button>
      </Stack>

      {/* Action required: DocuSign */}
      {signCount > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            pl: 2.5,
            mb: 3,
            borderRadius: 1.5,
            bgcolor: (theme) => theme.palette.accent.blue.bg,
            borderLeft: (theme) => `4px solid ${theme.palette.accent.blue.border}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <DescriptionOutlinedIcon
              sx={{
                fontSize: 20,
                mt: 0.25,
                flexShrink: 0,
                color: (theme) => theme.palette.accent.blue.action,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: (theme) => theme.palette.accent.blue.title,
                }}
              >
                Action Required: Signature Needed
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  mt: 0.5,
                  color: (theme) => theme.palette.accent.blue.text,
                }}
              >
                {`You have ${signCount} loan${
                  signCount > 1 ? "s" : ""
                } approved and ready for DocuSign.`}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                {signLoans.length > 0 ? (
                  signLoans.map((loan) => (
                    <Button
                      key={loan._id || loan.loanId}
                      variant="contained"
                      sx={{
                        height: 32,
                        px: 1.5,
                        borderRadius: 1.25,
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "none",
                        color: "common.white",
                        bgcolor: (theme) => theme.palette.accent.blue.action,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {`Sign ${loan.loanId}`}
                    </Button>
                  ))
                ) : (
                  // The signature-ready loans sit outside the active tab.
                  <Button
                    variant="contained"
                    onClick={() => setTab("sign")}
                    sx={{
                      height: 32,
                      px: 1.5,
                      borderRadius: 1.25,
                      fontSize: 14,
                      fontWeight: 600,
                      textTransform: "none",
                      color: "common.white",
                      bgcolor: (theme) => theme.palette.accent.blue.action,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                        boxShadow: "none",
                      },
                    }}
                  >
                    Review Signature Requests
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Status tabs */}
      <Box
        sx={{
          display: "flex",
          gap: 0.25,
          p: 0.5,
          mb: 3,
          height: 36,
          borderRadius: 2,
          bgcolor: "background.tabTrack",
        }}
      >
        {TABS.map((t) => (
          <TabPill
            key={t.key}
            active={tab === t.key}
            label={`${t.label} (${Number(counts[t.key]) || 0})`}
            onClick={() => setTab(t.key)}
          />
        ))}
      </Box>

      {/* Sign tab: signature cards rather than the table */}
      {tab === "sign" ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: (theme) => `1px solid ${theme.palette.accent.blue.borderSoft}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
            <DescriptionOutlinedIcon
              sx={{
                fontSize: 20,
                color: (theme) => theme.palette.accent.blue.action,
              }}
            />
            <Typography sx={{ fontSize: 16, color: "text.strong" }}>
              Awaiting Your Signature
            </Typography>
          </Stack>

          {isFetching && loans.length === 0 ? (
            <Stack alignItems="center" sx={{ py: 5 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : isError ? (
            <Typography
              sx={{ py: 5, textAlign: "center", fontSize: 14, color: "error.main" }}
            >
              Could not load your loans. Please try again.
            </Typography>
          ) : loans.length === 0 ? (
            <Typography
              sx={{ py: 5, textAlign: "center", fontSize: 14, color: "text.muted" }}
            >
              No loans are awaiting your signature.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {loans.map((loan) => (
                <SignatureCard
                  key={loan._id || loan.loanId}
                  loan={loan}
                  onSign={setSignLoan}
                />
              ))}
            </Stack>
          )}
        </Paper>
      ) : (
      /* Loan applications */
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.strong" }}>
            {TAB_HEADINGS[tab]}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      py: 1.25,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "text.strong",
                      whiteSpace: "nowrap",
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.border.light}`,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => {
                const cfg = statusConfig(loan.status);
                return (
                  <TableRow
                    key={loan._id || loan.loanId}
                    hover
                    sx={{
                      "&:hover": { bgcolor: (theme) => theme.palette.table.rowHover },
                      "& td": {
                        py: 1.75,
                        fontSize: 14,
                        borderColor: (theme) => theme.palette.border.light,
                      },
                      "&:last-of-type td": { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: (theme) => theme.palette.accent.cyan.deep,
                        }}
                      >
                        {loan.loanId}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ color: "text.strong", whiteSpace: "nowrap" }}>
                      {formatAmount(loan.amount, loan.currency)}
                    </TableCell>
                    <TableCell sx={{ color: "text.strong" }}>
                      {formatCollateral(loan)}
                    </TableCell>
                    <TableCell sx={{ color: "text.strong", whiteSpace: "nowrap" }}>
                      {formatApplied(loan.appliedAt)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={loan.status} />
                    </TableCell>
                    <TableCell>
                      <RowAction action={cfg?.action} />
                    </TableCell>
                  </TableRow>
                );
              })}

              {isFetching && loans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ border: 0, py: 5 }}>
                    <Stack alignItems="center">
                      <CircularProgress size={28} />
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {isError && !isFetching && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    sx={{
                      border: 0,
                      py: 5,
                      textAlign: "center",
                      fontSize: 14,
                      color: "error.main",
                    }}
                  >
                    Could not load your loans. Please try again.
                  </TableCell>
                </TableRow>
              )}

              {!isFetching && !isError && loans.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    sx={{
                      border: 0,
                      py: 5,
                      textAlign: "center",
                      fontSize: 14,
                      color: "text.muted",
                    }}
                  >
                    No loan applications in this category.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      <InvestorLoanApplicationModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
      />

      <InvestorSignDocumentsModal
        open={Boolean(signLoan)}
        loan={signLoan}
        onClose={() => setSignLoan(null)}
      />
    </Box>
  );
};

export default InvestorMortgage;
